import React, { useEffect, useState } from 'react'
import { FlatList, LayoutAnimation, Platform, Vibration, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { shuffle } from 'lodash'

import { GameQuestion } from '@trivie/core/src/models/game/GameQuestion'
import { Branding } from '@trivie/core/src/models/Branding'

import { Button } from '../button'
import { Text } from '../text'
import { color } from '../../theme/color'

import DraggableFlatList, { RenderItemParams } from 'react-native-draggable-flatlist'
import { LinearTransition } from 'react-native-reanimated'
import { SvgIcon } from '../svg-icon'
import { iconDragDots } from '../../assets'

interface MatchingChoicesProps {
  question: Instance<typeof GameQuestion>
  branding: Instance<typeof Branding>
  style?: ViewStyle
}

export const MatchingChoices = observer((props: MatchingChoicesProps) => {
  const { question, branding, style = {} } = props
  const { setMaxChoiceHeight, maxChoiceHeight } = question
  const [dataRight, setRightData] = useState(shuffle(question.rightMatching))
  const [selectedChoice, setSelectedChoice] = useState<null | number>()
  const [targetChoice, setTargetChoice] = useState(false)

  const submitBtnStyle = {
    backgroundColor: question.answered
      ? question.answeredCorrectly
        ? branding.correct_answer
        : branding.incorrect_answer
      : branding.accent_color,
  }

  const handleSubmit = () => {
    if (question.answered) {
      return
    }
    question.setUserMatching(question.pairs.map((p, i) => ({ left: p.left, right: dataRight[i] })))
    question.answer()
  }

  const handlePressLeft = (index) => {
    if (targetChoice) {
      return
    }

    if (selectedChoice === index) {
      setSelectedChoice(null)
    } else {
      setSelectedChoice(index)
    }
  }

  const handlePressRight = (index: number) => {
    if (targetChoice) {
      return
    }

    if (selectedChoice !== null && selectedChoice !== undefined && selectedChoice !== index) {
      setTargetChoice(true)
      const reorder = [...dataRight]
      ;[reorder[selectedChoice], reorder[index]] = [reorder[index], reorder[selectedChoice]]

      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      setRightData(reorder)

      setTimeout(() => {
        setSelectedChoice(null)
        setTargetChoice(false)
      }, 1000)
    } else if (
      selectedChoice !== null &&
      selectedChoice !== undefined &&
      selectedChoice === index
    ) {
      setTargetChoice(true)
      setTimeout(() => {
        setSelectedChoice(null)
        setTargetChoice(false)
      }, 1000)
    }
  }

  const renderRight = ({ item, getIndex, drag, isActive }: RenderItemParams<string>) => {
    return (
      <Button
        onLayout={(e) => {
          if (Platform.OS !== 'web') {
            const { height } = e.nativeEvent.layout
            if (height > Number(maxChoiceHeight)) {
              setMaxChoiceHeight(height)
            }
          }
        }}
        // @ts-ignore
        style={{
          ...CHOICE_BTN,
          borderRightWidth: 20,
          paddingRight: 0,
          cursor: selectedChoice !== null && selectedChoice !== undefined ? 'pointer' : 'move',
          borderColor:
            targetChoice && selectedChoice === getIndex()
              ? branding.text_color
              : question.answered
                ? question.pairs[getIndex()!].matchedCorrectly
                  ? branding.correct_answer
                  : branding.incorrect_answer
                : branding.accent_color,
          opacity: maxChoiceHeight ? 1.0 : 0,
          height: maxChoiceHeight ? maxChoiceHeight : 'auto',
          backgroundColor: branding.quiz_background,
        }}
        key={item}
        variant="outlined"
        fullWidth
        round
        onPressIn={selectedChoice !== null && selectedChoice !== undefined ? undefined : drag}
        onPress={() => handlePressRight(getIndex()!)}
      >
        <View style={CHOICE_CONTAINER}>
          <View
            onLayout={(e) => {
              if (Platform.OS === 'web') {
                const { height } = e.nativeEvent.layout
                if (height > Number(maxChoiceHeight)) {
                  setMaxChoiceHeight(height + 12)
                }
              }
            }}
            style={CHOICE_TXT_CONTAINER}
          >
            <Text
              disableLink
              variant="body1Medium"
              align="center"
              style={{ color: branding.text_color }}
              text={item}
              adjustsFontSizeToFit
              numberOfLines={!item?.includes(' ') && item?.length < 18 ? 1 : 4}
            />
          </View>
          <View style={{ position: 'absolute', right: -22 }}>
            {Platform.OS === 'web' ? (
              <svg width="24" height="25" viewBox="0 0 24 25" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_211_23440)">
                  <path
                    fill={
                      targetChoice && selectedChoice === getIndex()
                        ? branding.quiz_background
                        : branding.text_color
                    }
                    d="M11 18.5C11 19.6 10.1 20.5 9 20.5C7.9 20.5 7 19.6 7 18.5C7 17.4 7.9 16.5 9 16.5C10.1 16.5 11 17.4 11 18.5ZM9 10.5C7.9 10.5 7 11.4 7 12.5C7 13.6 7.9 14.5 9 14.5C10.1 14.5 11 13.6 11 12.5C11 11.4 10.1 10.5 9 10.5ZM9 4.5C7.9 4.5 7 5.4 7 6.5C7 7.6 7.9 8.5 9 8.5C10.1 8.5 11 7.6 11 6.5C11 5.4 10.1 4.5 9 4.5ZM15 8.5C16.1 8.5 17 7.6 17 6.5C17 5.4 16.1 4.5 15 4.5C13.9 4.5 13 5.4 13 6.5C13 7.6 13.9 8.5 15 8.5ZM15 10.5C13.9 10.5 13 11.4 13 12.5C13 13.6 13.9 14.5 15 14.5C16.1 14.5 17 13.6 17 12.5C17 11.4 16.1 10.5 15 10.5ZM15 16.5C13.9 16.5 13 17.4 13 18.5C13 19.6 13.9 20.5 15 20.5C16.1 20.5 17 19.6 17 18.5C17 17.4 16.1 16.5 15 16.5Z"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_211_23440">
                    <rect
                      fill={
                        targetChoice && selectedChoice === getIndex()
                          ? branding.quiz_background
                          : branding.text_color
                      }
                      width="24"
                      height="24"
                      transform="translate(0 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : (
              <SvgIcon
                size={24}
                src={iconDragDots}
                color={
                  targetChoice && selectedChoice === getIndex()
                    ? branding.quiz_background
                    : branding.text_color
                }
              />
            )}
          </View>
        </View>
      </Button>
    )
  }

  const renderLeft = ({ item, index }) => {
    return (
      <Button
        onLayout={(e) => {
          if (Platform.OS !== 'web') {
            const { height } = e.nativeEvent.layout
            if (height > Number(maxChoiceHeight)) {
              setMaxChoiceHeight(height + 12)
            }
          }
        }}
        onPress={() => handlePressLeft(index)}
        style={{
          ...CHOICE_BTN,
          borderColor:
            selectedChoice === index
              ? branding.text_color
              : question.answered
                ? question.pairs[index].matchedCorrectly
                  ? branding.correct_answer
                  : branding.incorrect_answer
                : branding.accent_color,
          opacity: maxChoiceHeight ? 1.0 : 0,
          height: maxChoiceHeight ? maxChoiceHeight : 'auto',
        }}
        key={item.left}
        variant="outlined"
        fullWidth
        round
      >
        <View
          onLayout={(e) => {
            if (Platform.OS === 'web') {
              const { height } = e.nativeEvent.layout
              if (height > Number(maxChoiceHeight)) {
                setMaxChoiceHeight(height + 12)
              }
            }
          }}
          style={{ ...CHOICE_TXT_CONTAINER, paddingRight: 0 }}
        >
          <Text
            disableLink
            variant="body1Medium"
            align="center"
            style={{ color: branding.text_color }}
            text={item.left}
            adjustsFontSizeToFit
            numberOfLines={!item.left?.includes(' ') && item.left?.length < 18 ? 1 : 4}
          />
        </View>

        <View
          style={{
            ...LINK,
            backgroundColor:
              selectedChoice === index
                ? branding.text_color
                : question.answered
                  ? question.answeredCorrectly
                    ? branding.correct_answer
                    : branding.incorrect_answer
                  : branding.accent_color,
          }}
        />
      </Button>
    )
  }

  return (
    <View style={{ ...LIST, ...style }}>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 0.9 }}>
          <FlatList
            data={question.pairs}
            scrollEnabled={false}
            contentContainerStyle={{ paddingTop: 10, paddingRight: 8 }}
            // keyExtractor={(item) => item.key}
            renderItem={renderLeft}
          />
        </View>
        <View style={{ flex: 1, paddingTop: 10 }}>
          <DraggableFlatList
            activationDistance={1}
            extraData={[dataRight?.toString()]}
            itemLayoutAnimation={LinearTransition}
            // onPlaceholderIndexChange={() => Vibration.vibrate(20)}
            renderPlaceholder={() => (
              <View style={{ ...PLACEHOLDER, borderColor: `${branding.accent_color}40` }} />
            )}
            enableLayoutAnimationExperimental={
              Platform.OS !== 'web' && selectedChoice !== null && selectedChoice !== undefined
                ? true
                : false
            }
            data={dataRight}
            contentContainerStyle={{ paddingLeft: 0 }}
            scrollEnabled={false}
            onDragEnd={({ data }) => setRightData(data)}
            keyExtractor={(item) => item}
            renderItem={renderRight}
          />
        </View>
      </View>
      <Button
        style={submitBtnStyle}
        textStyle={{ color: color.white }}
        fullWidth
        tx="Common.submit"
        onPress={handleSubmit}
      />
    </View>
  )
})

const LIST: ViewStyle = {
  maxWidth: 800,
  width: '100%',
  alignSelf: 'center',
  gap: 16,
  justifyContent: 'flex-end',
}
const PLACEHOLDER: ViewStyle = {
  borderWidth: 2,
  flex: 1,
  borderColor: color.tint20,
  borderStyle: 'dashed',
  padding: 16,
  borderRadius: 24,
  justifyContent: 'center',
  marginVertical: 8,
  paddingHorizontal: 8,
}
const CHOICE_BTN: ViewStyle = {
  borderWidth: 2,
  borderRadius: 24,
  justifyContent: 'center',
  marginVertical: 8,
  zIndex: 3,
  minHeight: 56,
  maxHeight: 120,
}
const LINK: ViewStyle = {
  height: 2,
  width: 20,
  position: 'absolute',
  right: -20,
  zIndex: 2,
}
const CHOICE_CONTAINER: ViewStyle = {
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
}
const CHOICE_TXT_CONTAINER: ViewStyle = {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 10,
}
